import { useContext, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import axios from "axios";
import { TitleContext } from "../components/Context";

import { downloadAnalyticsQr, getAnalyticsSummary, type AnalyticsSummary } from "../services/analyticsApi";

function isoDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function formatAction(action: string): string {
  return action.replaceAll("_", " ");
}

export default function AnalyticsPage() {
  const { setTitle } = useContext(TitleContext);
  useEffect(() => {
    setTitle("Analítica de uso");
  }, [setTitle]);
  const today = useMemo(() => new Date(), []);
  const [rangeDays, setRangeDays] = useState("30");
  const [from, setFrom] = useState(isoDate(new Date(today.getTime() - 30 * 86400000)));
  const [to, setTo] = useState(isoDate(today));
  const [source, setSource] = useState("");
  const [medium, setMedium] = useState("");
  const [campaign, setCampaign] = useState("");
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkSource, setLinkSource] = useState("anp");
  const [linkMedium, setLinkMedium] = useState("referral");
  const [linkCampaign, setLinkCampaign] = useState("portal");
  const [linkContent, setLinkContent] = useState("");
  const [destination, setDestination] = useState("/dashboard");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);

  const loadSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const loaded = await getAnalyticsSummary({
        from: `${from}T00:00:00.000Z`,
        to: `${to}T23:59:59.999Z`,
        source: source || undefined,
        medium: medium || undefined,
        campaign: campaign || undefined,
      });
      setSummary(loaded);
    } catch (caught) {
      if (axios.isAxiosError(caught) && caught.response?.status === 403) {
        setError("Tu usuario no tiene permiso para consultar la analítica.");
      } else {
        setError("No se pudo cargar la analítica.");
      }
    } finally {
      setLoading(false);
    }
  };

  const applyRange = (days: string) => {
    setRangeDays(days);
    if (days === "custom") return;
    const numericDays = Number(days);
    const end = new Date();
    setFrom(isoDate(new Date(end.getTime() - numericDays * 86400000)));
    setTo(isoDate(end));
  };

  const referenceUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (linkSource) params.set("utm_source", linkSource);
    if (linkMedium) params.set("utm_medium", linkMedium);
    if (linkCampaign) params.set("utm_campaign", linkCampaign);
    if (linkContent) params.set("utm_content", linkContent);
    return `${window.location.origin}${destination}?${params.toString()}`;
  }, [destination, linkCampaign, linkContent, linkMedium, linkSource]);

  const generateQr = async () => {
    setQrLoading(true);
    setQrError(null);
    try {
      const blob = await downloadAnalyticsQr(referenceUrl);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = "protea-referencia.svg";
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setQrError("No se pudo generar el QR. Verifica la instalación del servicio.");
    } finally {
      setQrLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: "auto" }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Analítica de visitas</Typography>
          <Typography color="text.secondary">
            Sesiones y eventos aproximados. No se identifican personas ni se calculan visitantes persistentes.
          </Typography>
        </Box>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Filtros</Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                {[["7", "7 días"], ["30", "30 días"], ["90", "90 días"], ["custom", "Personalizado"]].map(([value, label]) => (
                  <Button key={value} variant={rangeDays === value ? "contained" : "outlined"} onClick={() => applyRange(value)}>
                    {label}
                  </Button>
                ))}
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}><TextField fullWidth type="date" label="Desde" value={from} onChange={(event) => setFrom(event.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth type="date" label="Hasta" value={to} onChange={(event) => setTo(event.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Origen" value={source} onChange={(event) => setSource(event.target.value)} placeholder="anp" /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Medio" value={medium} onChange={(event) => setMedium(event.target.value)} placeholder="referral" /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Campaña" value={campaign} onChange={(event) => setCampaign(event.target.value)} placeholder="portal" /></Grid>
              </Grid>
              <Button variant="contained" onClick={() => void loadSummary()} disabled={loading}>{loading ? "Cargando…" : "Aplicar filtros"}</Button>
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </CardContent>
        </Card>
        {summary && (
          <>
            <Grid container spacing={2}>
              {[["Sesiones", summary.sessions], ["Eventos", summary.events], ["Visitantes", "No estimados"]].map(([label, value]) => (
                <Grid item xs={12} md={4} key={label}>
                  <Card><CardContent><Typography color="text.secondary">{label}</Typography><Typography variant="h3" fontWeight={800}>{value}</Typography></CardContent></Card>
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}><AnalyticsList title="Sesiones por origen" rows={summary.by_source.map((row) => [row.source, row.sessions])} /></Grid>
              <Grid item xs={12} md={4}><AnalyticsList title="Sesiones por campaña" rows={summary.by_campaign.map((row) => [row.campaign, row.sessions])} /></Grid>
              <Grid item xs={12} md={4}><AnalyticsList title="Sesiones por vista" rows={summary.by_device_class.map((row) => [row.device_class, row.sessions])} /></Grid>
              <Grid item xs={12} md={4}><AnalyticsList title="Eventos" rows={Object.entries(summary.event_counts)} /></Grid>
            </Grid>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Origen × estación × acción</Typography>
                <Table size="small"><TableHead><TableRow><TableCell>Origen</TableCell><TableCell>Estación</TableCell><TableCell>Acción</TableCell><TableCell align="right">Eventos</TableCell></TableRow></TableHead><TableBody>
                  {summary.matrix.map((row) => <TableRow key={`${row.source}-${row.place_id}-${row.action}`}><TableCell>{row.source}</TableCell><TableCell>{row.place_id}</TableCell><TableCell>{formatAction(row.action)}</TableCell><TableCell align="right">{row.events}</TableCell></TableRow>)}
                  {summary.matrix.length === 0 && <TableRow><TableCell colSpan={4}>No hay eventos con estación en el rango.</TableCell></TableRow>}
                </TableBody></Table>
              </CardContent>
            </Card>
          </>
        )}
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Generador de enlaces y QR</Typography>
              <Typography color="text.secondary">Los parámetros se limitan a la atribución; no incluyas datos personales, tokens ni texto libre.</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}><TextField fullWidth label="Origen" value={linkSource} onChange={(event) => setLinkSource(event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Medio" value={linkMedium} onChange={(event) => setLinkMedium(event.target.value)} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Campaña" value={linkCampaign} onChange={(event) => setLinkCampaign(event.target.value)} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Contenido" value={linkContent} onChange={(event) => setLinkContent(event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Destino" value={destination} onChange={(event) => setDestination(event.target.value.startsWith("/") ? event.target.value : `/${event.target.value}`)} /></Grid>
              </Grid>
              <Chip label={referenceUrl} variant="outlined" sx={{ width: "fit-content", maxWidth: "100%" }} />
              <Stack direction="row" spacing={2}><Button variant="contained" onClick={() => void navigator.clipboard?.writeText(referenceUrl)}>Copiar URL</Button><Button variant="outlined" onClick={() => void generateQr()} disabled={qrLoading}>{qrLoading ? "Generando…" : "Descargar QR"}</Button></Stack>
              {qrError && <Alert severity="warning">{qrError}</Alert>}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

function AnalyticsList({ title, rows }: { title: string; rows: Array<[string, string | number]> }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Stack divider={<Divider />}>
          {rows.length === 0 && <Typography color="text.secondary">Sin datos</Typography>}
          {rows.map(([label, value]) => <Stack key={label} direction="row" justifyContent="space-between" py={1}><Typography>{label}</Typography><Typography fontWeight={700}>{value}</Typography></Stack>)}
        </Stack>
      </CardContent>
    </Card>
  );
}
